import api from '../../../components/services/apiService';
import { PaginationInfo, Vulnerability, VulnerabilityBannerResponse, VulnerabilityStats } from '../Utils/types';

type FetchOsUpdateVulnerabilitiesParams = {
  tenantId: string;
  source: string;
  severity?: string;
  pageNumber: number;
  pageSize: number;
  search?: string;
};

type FetchOsUpdateVulnerabilitiesResult = {
  vulnerabilities: Vulnerability[];
  paginationInfo: PaginationInfo | null;
};

type FetchOsUpdateVulnerabilitiesBannerParams = {
  tenantId: string;
  source: string;
};

const normalizeBannerStats = (data: any): VulnerabilityStats => ({
  totalVulnerabilities: Number(data?.totalVulnerabilities ?? 0),
  totalKEV: Number(data?.totalKEV ?? data?.totalKev ?? 0),
  totalCritical: Number(data?.totalCritical ?? 0),
  totalEPSSAboveThreshold: Number(data?.totalEPSSAboveThreshold ?? data?.totalEpssAboveThreshold ?? 0),
  affectedDevices: Number(data?.affectedDevices ?? 0),
});

const normalizeSourceForQuery = (source: string): string | undefined => {
  const normalized = source?.trim();
  if (!normalized) return undefined;
  if (normalized.toLowerCase() === 'patchifi') return undefined;
  return normalized.toUpperCase().replace(/\s+/g, '');
};

const getAgeInDays = (publishedDateValue?: string) => {
  if (!publishedDateValue) return 0;
  const publishedDate = new Date(publishedDateValue);
  if (Number.isNaN(publishedDate.getTime())) return 0;
  const today = new Date();
  return Math.floor((today.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24));
};

const toDisplaySeverity = (severity?: string) => {
  if (!severity) return 'Unknown';
  const normalized = severity.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const toNumber = (value: unknown, fallback = 0): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const buildRowKey = (item: any, pageNumber: number, index: number) => {
  const compositeKey = [
    'OS',
    item.softwareAssetInventoryId,
    item.cveId || item.cveID,
    item.packageIdentifier,
    item.kbId || item.KBId,
    item.version,
    item.title || item.name,
    item.cveUrl,
  ]
    .filter(Boolean)
    .join('::');

  return compositeKey || `OS::page-${pageNumber}::row-${index}`;
};

const mapOsItemToVulnerability = (item: any, pageNumber: number, index: number): Vulnerability => {
  const kbId = item.kbId || item.KBId || item.kbID || '';
  const packageIdentifier = item.packageIdentifier || kbId || item.cveId || item.cveID || `os-${index}`;

  return {
    rowKey: buildRowKey(item, pageNumber, index),
    softwareAssetInventoryId: String(item.softwareAssetInventoryId || `${packageIdentifier}-${index}`),
    cveId: item.cveId || item.cveID || 'N/A',
    cveUrl: item.cveUrl,
    title: item.title || item.name || kbId || 'OS Update Vulnerability',
    severity: toDisplaySeverity(item.severity || item.baseSeverity),
    cvssScore: toNumber(item.cvssScore ?? item.cvss),
    epssScore: toNumber(item.epssScore),
    kev: item.isKEV ?? item.kev ?? item.isKev ?? item.knownExploited ?? null,
    affectedDevicesCount: toNumber(
      item.affectedDevicesCount ?? item.totalAffectedDevices ?? item.totalEndpointsAffected,
      toNumber(item.installedEndpoints) + toNumber(item.installedServers),
    ),
    ageInDays: typeof item.ageInDays === 'number' ? item.ageInDays : getAgeInDays(item.cveDatePublished || item.publishedDate),
    description: item.description || item.descriptions,
    version: item.version || item.currentVersion,
    packageIdentifier,
    includedAgentIds: Array.isArray(item.includedAgentIds)
      ? item.includedAgentIds.filter((agentId: unknown): agentId is string => typeof agentId === 'string' && !!agentId)
      : [],
    updateType: 'OS',
    kbId,
    fixVersion: item.fixVersion || item.category || item.latestVersion || '',
  };
};

export const fetchOsUpdateVulnerabilities = async (
  params: FetchOsUpdateVulnerabilitiesParams,
): Promise<FetchOsUpdateVulnerabilitiesResult> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(params.pageNumber),
    pageSize: String(params.pageSize),
  });

  const sourceParam = normalizeSourceForQuery(params.source);
  if (sourceParam) queryParams.set('source', sourceParam);

  if (params.severity?.trim()) {
    queryParams.set('severity', params.severity.trim().toUpperCase());
  }

  if (params.search?.trim()) {
    queryParams.set('search', params.search.trim());
  }

  const response = await api.get(`vulnerabilityservice/os-update/${params.tenantId}/vulnerabilities?${queryParams.toString()}`);
  const payload = response?.data ?? {};
  const data = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];

  const mappedData = data.map((item: any, index: number) => mapOsItemToVulnerability(item, params.pageNumber, index));
  const pagination = payload?.pagination;

  const paginationInfo: PaginationInfo = {
    pageNumber: pagination?.pageNumber ?? params.pageNumber,
    pageSize: pagination?.pageSize ?? params.pageSize,
    totalRecords: pagination?.totalRecords ?? mappedData.length,
    totalPages: pagination?.totalPages ?? Math.max(1, Math.ceil(mappedData.length / params.pageSize)),
    hasPreviousPage: pagination?.hasPreviousPage ?? params.pageNumber > 1,
    hasNextPage:
      pagination?.hasNextPage ??
      (pagination?.totalPages ? params.pageNumber < pagination.totalPages : mappedData.length >= params.pageSize),
  };

  return {
    vulnerabilities: mappedData,
    paginationInfo,
  };
};

export const fetchOsUpdateVulnerabilitiesBanner = async (
  params: FetchOsUpdateVulnerabilitiesBannerParams,
): Promise<VulnerabilityBannerResponse> => {
  const queryParams = new URLSearchParams();
  const sourceParam = normalizeSourceForQuery(params.source);

  // Patchifi must not send `source` in query for OS banner endpoint.
  if (sourceParam) {
    queryParams.set('source', sourceParam);
  }

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `vulnerabilityservice/os-update/${params.tenantId}/vulnerabilities/banner?${queryString}`
    : `vulnerabilityservice/os-update/${params.tenantId}/vulnerabilities/banner`;

  const response = await api.get(endpoint);
  const payload = response?.data ?? {};
  const statsSource = payload?.data ?? payload;

  return {
    data: normalizeBannerStats(statsSource),
    message: payload?.message ?? '',
    isSuccess: payload?.isSuccess ?? true,
    errorCode: payload?.errorCode ?? null,
  };
};
